import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore, useAuthHydrated } from '../model/authStore';
import { canPerform } from '@aegislab/shared';
import type { AuthUser } from '@aegislab/shared';

/**
 * Access check function — receives authenticated user, returns true if allowed.
 */
export type AccessCheck = (user: AuthUser) => boolean;

interface RouteGuardResult {
  user: AuthUser | null;
  allowed: boolean;
}

/**
 * Centralized route guard hook.
 * - Waits for zustand persist hydration before any redirect
 * - Not logged in → redirect to /login
 * - Logged in but access check fails → redirect to /
 * - Returns { user, allowed }
 *
 * Usage:
 *   const { user, allowed } = useRouteGuard(requireGlobalQuestAccess);
 *   if (!allowed) return null;
 */
export function useRouteGuard(check?: AccessCheck): RouteGuardResult {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthHydrated();
  const navigate = useNavigate();

  const allowed = !!user && (!check || check(user));

  useEffect(() => {
    if (!hydrated) return; // Wait for localStorage rehydration
    if (!user) {
      void navigate({ to: '/login' });
    } else if (!allowed) {
      void navigate({ to: '/', search: {} });
    }
  }, [user, allowed, navigate, hydrated]);

  return { user, allowed: hydrated && allowed };
}

// ─── Pre-built access checks (backed by shared permission system) ───

/** ADMIN, OPERATOR, or team LEADER */
export const requireManager: AccessCheck = (user) => canPerform(user, 'attendance', 'manage');

/** ADMIN only */
export const requireGlobalQuestAccess: AccessCheck = (user) =>
  canPerform(user, 'quest', 'outbreak');

/** Can manage teams — ADMIN or OPERATOR */
export const requireTeamAdmin: AccessCheck = (user) => canPerform(user, 'team', 'update');
